import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown, Coins } from "lucide-react";
import { useLocale } from "../context/LocaleContext";
import { LANGUAGES, flagUrl } from "../i18n/config";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuItem, DropdownMenuSub, DropdownMenuSubTrigger,
  DropdownMenuSubContent, DropdownMenuPortal,
} from "./ui/dropdown-menu";

export const Flag = ({ code, className = "w-5 h-[15px]" }) => (
  <img
    src={flagUrl(code, 40)}
    srcSet={`${flagUrl(code, 80)} 2x`}
    alt=""
    aria-hidden="true"
    className={`${className} rounded-[2px] object-cover shrink-0 shadow-sm`}
    loading="lazy"
  />
);

export const LocaleSwitcher = ({ variant = "nav", showCurrency = true }) => {
  const { t } = useTranslation();
  const { language, setLanguage, currency, currencyInfo, setCurrency, currencies } = useLocale();
  const current = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  const triggerClass = variant === "nav"
    ? "inline-flex items-center gap-2 h-9 px-2.5 rounded-lg border border-border/60 bg-card/60 hover:bg-secondary text-sm transition-colors"
    : "inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-border bg-card hover:bg-secondary text-sm transition-colors";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="locale-switcher-trigger" className={triggerClass} aria-label={t("locale.language")}>
          <Flag code={current.flag} />
          <span className="hidden sm:inline font-medium" lang={current.code}>{current.native}</span>
          {showCurrency && (
            <span className="hidden md:inline text-xs text-muted-foreground font-mono">· {currencyInfo.code}</span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 max-h-[80vh] overflow-y-auto" data-testid="locale-switcher-menu">
        <DropdownMenuLabel className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
          <Globe className="w-3.5 h-3.5" /> {t("locale.language")}
        </DropdownMenuLabel>
        {LANGUAGES.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`language-option-${l.code}`}
            onClick={() => setLanguage(l.code)}
            className="flex items-center gap-3 cursor-pointer"
          >
            <Flag code={l.flag} />
            <span className="flex-1" lang={l.code} dir={l.rtl ? "rtl" : "ltr"}>{l.native}</span>
            <span className="text-xs text-muted-foreground">{l.name}</span>
            {l.code === language && <Check className="w-4 h-4 text-primary" />}
          </DropdownMenuItem>
        ))}
        {showCurrency && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger data-testid="currency-submenu-trigger" className="flex items-center gap-3 cursor-pointer">
                <Coins className="w-4 h-4 text-muted-foreground" />
                <span className="flex-1">{t("locale.currency")}</span>
                <span className="text-xs font-mono text-muted-foreground">{currency}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent className="w-60 max-h-[70vh] overflow-y-auto" data-testid="currency-submenu">
                  {currencies.map((c) => (
                    <DropdownMenuItem
                      key={c.code}
                      data-testid={`currency-option-${c.code}`}
                      onClick={() => setCurrency(c.code)}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <Flag code={c.flag} />
                      <span className="flex-1">{t(`currencies.${c.code}`)}</span>
                      <span className="text-xs font-mono text-muted-foreground">{c.code}</span>
                      {c.code === currency && <Check className="w-4 h-4 text-primary" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const CurrencySelector = () => {
  const { t } = useTranslation();
  const { currency, setCurrency, currencies } = useLocale();
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="currency-selector">
      <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">{t("locale.currency")}</span>
      {currencies.map((c) => (
        <button
          key={c.code}
          type="button"
          data-testid={`currency-chip-${c.code}`}
          onClick={() => setCurrency(c.code)}
          className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full border text-xs font-mono transition-colors ${
            c.code === currency ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-secondary"
          }`}
          title={t(`currencies.${c.code}`)}
        >
          <Flag code={c.flag} className="w-4 h-3" />
          {c.code}
        </button>
      ))}
    </div>
  );
};
